import { shortDateTime } from '../../shared/dateTime';
import type { PlanItem, PlanSegment } from './vehiclePlanApi';

/**
 * Okna klienta jako nawiasy Z/R na wykresach FTL — czysta logika
 * (24.10.2026, S-24.10-05, przełącznik `ganttWindowBrackets`).
 *
 * Paczka p. Mariusza z 21.10 (`dokumentacja/decyzje.md`, 20.10 i 21.10):
 * „nawias Z obejmuje dozwolone położenie segmentu załadunku, nawias R
 * segmentu rozładunku … przy kilku punktach Z1, R1, R2…; godziny w chmurce
 * nawiasu …; okno poza zakresem osi jako nawias otwarty ze strzałką;
 * kolizja = nawias czerwony; nawias nieprzeciągalny; linia min. 2 px,
 * kontrast min. 3:1”. Na harmonogramie floty — przy każdym zleceniu.
 *
 * Które okno stoi przy którym punkcie, mówi API (`window-brackets.ts`):
 * termin z planu TMS, a bez niego okno zlecenia od klienta. Tu: kolor,
 * kolizja z odcinkami szacunku i chmurka. Pasek zostaje szacunkiem tylko do
 * odczytu — przesuwanie i korekta czasów (BR-FTL-19…23) to osobna pozycja.
 *
 * ## Kolizja
 *
 * Odcinki szacunku (`plan-segments.ts`) mają jeden załadunek i jeden
 * rozładunek: pierwszy załadunek i ostatni rozładunek trasy. Punkty pośrednie
 * są doliczane przed trasą jednym odcinkiem, bez miejsca na osi — przy ich
 * nawiasach kolizji nie oceniamy i chmurka to mówi. Szacunek wykonania po
 * końcu okna rozładunku (`overrunUntil`) to też kolizja ostatniego R.
 * Pozycja bez odcinków (trasa nieznana) — bez oceny, nie zgadujemy.
 */

export type BracketKind = 'loading' | 'unloading';

/** Nawias z API (`WindowBracket`), daty jako ISO. */
export interface WindowBracket {
  kind: BracketKind;
  index: number;
  label: string;
  from: string | null;
  to: string | null;
  source: 'tmsPlan' | 'client';
  place: string | null;
}

/**
 * Kolory linii nawiasu na białym tle: brąz (Z) i fiolet (R) nie mylą się
 * z kolorami linii osi (niebieski, zielony, szary) ani z pomarańczem
 * ostrzeżeń; czerwień — kolizja. Każdy ma kontrast z bielą co najmniej 3:1
 * (pilnuje test).
 */
export const BRACKET_COLORS = {
  loading: '#4e342e',
  unloading: '#6a1b9a',
  conflict: '#c62828',
} as const;

/** Grubość linii nawiasu (px) — decyzja z 20.10: „linia min. 2 px”. */
export const BRACKET_LINE_PX = 2;

/** Identyfikatory kontraktu paczki (rejestr 0.11, ścieżka FTL-14) — nasze nawiasy je niosą. */
export const bracketContractTestIds = ['ftl-realizacja-nawias-z-1', 'ftl-realizacja-nawias-r-1'] as const;

/** `data-testid` nawiasu: `ftl-realizacja-nawias-z-1`, `…-r-2` (kontrakt paczki). */
export const bracketTestId = (b: Pick<WindowBracket, 'kind' | 'index'>): string => `ftl-realizacja-nawias-${b.kind === 'loading' ? 'z' : 'r'}-${b.index}`;

const ms = (v: string | null | undefined): number | null => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

/** Nawiasy pozycji z datami (bez dat nie rysujemy). */
export function drawableBrackets(item: Pick<PlanItem, 'windowBrackets'>): WindowBracket[] {
  return (item.windowBrackets ?? []).filter((b) => ms(b.from) !== null && ms(b.to) !== null);
}

/** Wynik oceny nawiasu wobec szacunku. */
export interface BracketCheck {
  /** `true` — szacunek wychodzi poza nawias. */
  conflict: boolean;
  /** Zdanie do chmurki: kolizja, zgodność albo czemu bez oceny. */
  text: string;
}

const range = (from: string, to: string) => `${shortDateTime(from)} – ${shortDateTime(to)}`;

function segmentOf(segments: readonly PlanSegment[], kind: 'loading' | 'unloading'): PlanSegment | null {
  const all = segments.filter((s) => s.kind === kind);
  if (all.length === 0) return null;
  return kind === 'loading' ? all[0] : all[all.length - 1];
}

/**
 * Czy odcinek szacunku mieści się w nawiasie. Ocenia się tylko pierwszy Z
 * (odcinek załadunku) i ostatni R (odcinek rozładunku i spóźnienie po oknie).
 */
export function checkBracket(
  item: Pick<PlanItem, 'segments' | 'overrunUntil' | 'windowBrackets'>,
  b: WindowBracket,
): BracketCheck {
  const brackets = item.windowBrackets ?? [];
  const sameKind = brackets.filter((x) => x.kind === b.kind);
  const evaluated = b.kind === 'loading' ? b.index === 1 : b.index === sameKind.length;
  if (!evaluated) {
    return {
      conflict: false,
      text: `Punkt pośredni — czas postoju doliczamy przed trasą, więc zgodności z oknem przy tym punkcie nie oceniamy.`,
    };
  }
  const segments = item.segments ?? [];
  const seg = segmentOf(segments, b.kind);
  const from = ms(b.from);
  const to = ms(b.to);
  if (!seg || from === null || to === null) {
    return {
      conflict: false,
      text: 'Szacunek nie jest rozpisany na odcinki (trasa nieznana) — zgodności z oknem nie oceniamy.',
    };
  }
  const word = b.kind === 'loading' ? 'załadunek' : 'rozładunek';
  const s = ms(seg.start) as number;
  let e = ms(seg.end) as number;
  if (b.kind === 'unloading') {
    const over = ms(item.overrunUntil ?? null);
    if (over !== null && over > e) e = over;
  }
  const end = new Date(e).toISOString();
  if (s < from || e > to) {
    return {
      conflict: true,
      text: `Kolizja: szacowany ${word} ${range(seg.start, end)} wypada poza oknem.`,
    };
  }
  return { conflict: false, text: `Szacowany ${word} ${range(seg.start, end)} mieści się w oknie.` };
}

const SOURCE_TEXT: Record<WindowBracket['source'], string> = {
  tmsPlan: 'termin z planu TMS',
  client: 'okno zlecenia od klienta',
};

/** Chmurka nawiasu: nazwa, godziny w czasie polskim, miejsce, źródło, ocena. */
export function bracketTip(item: Pick<PlanItem, 'number' | 'orderId' | 'segments' | 'overrunUntil' | 'windowBrackets'>, b: WindowBracket): string {
  const what = b.kind === 'loading' ? 'okno załadunku' : 'okno rozładunku';
  const lines = [
    `${b.label} — ${what}${b.place ? `, ${b.place}` : ''}`,
    `${b.from && b.to ? range(b.from, b.to) : '—'} (${SOURCE_TEXT[b.source]})`,
    `Zlecenie ${item.number ?? `#${item.orderId}`}`,
    checkBracket(item, b).text,
  ];
  return lines.join('\n');
}

/** Położenie nawiasu względem osi: w kadrze (może być ucięty) albo cały przed / za nim. */
export type BracketPlacement = 'inView' | 'before' | 'after';

export function bracketPlacement(b: WindowBracket, window: { from: Date; to: Date }): BracketPlacement {
  const f = ms(b.from) as number;
  const t = ms(b.to) as number;
  if (t < window.from.getTime()) return 'before';
  if (f > window.to.getTime()) return 'after';
  return 'inView';
}

/** Zasięg nawiasów pozycji — do rozkładu na podtory. */
export function bracketsExtent(item: Pick<PlanItem, 'windowBrackets'>): number[] {
  return drawableBrackets(item).flatMap((b) => [ms(b.from) as number, ms(b.to) as number]);
}

/** Kontrast dwóch kolorów `#rrggbb` (WCAG 2.x) — do testu „min. 3:1”. */
export function contrastRatio(a: string, b: string): number {
  const lum = (hex: string) => {
    const [r, g, bl] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
    const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(bl);
  };
  const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

// ── Oś czasu na karcie zlecenia ─────────────────────────────────────────

/** Punkt trasy z karty zlecenia (`keyPoints` w szczegółach). */
interface RecordPoint {
  type?: string | null;
  pointOrder?: number | null;
  routerPlannedFrom?: string | null;
  routerPlannedTo?: string | null;
  city?: string | null;
  countryCode?: string | null;
}

/**
 * Nawiasy zlecenia z karty — ta sama reguła co API (`window-brackets.ts`,
 * `windowBracketsOf`): termin z planu TMS przy punkcie, brak krańca —
 * z okna klienta; punkt bez terminu — okno klienta; bez punktów danego
 * rodzaju — jeden nawias z okna klienta. Karta zlecenia niesie punkty
 * w surowej postaci, więc oś czasu liczy to sama (test pilnuje tych samych
 * przypadków co w API).
 */
export function recordBrackets(record: {
  fromDate?: unknown;
  toDate?: unknown;
  keyPoints?: unknown;
}): WindowBracket[] {
  const clientFrom = typeof record.fromDate === 'string' ? record.fromDate : null;
  const clientTo = typeof record.toDate === 'string' ? record.toDate : null;
  const points = (Array.isArray(record.keyPoints) ? (record.keyPoints as RecordPoint[]) : [])
    .slice()
    .sort((a, b) => (a.pointOrder ?? 0) - (b.pointOrder ?? 0));
  const out: WindowBracket[] = [];
  for (const kind of ['loading', 'unloading'] as const) {
    const ofKind = points.filter((p) => p.type === kind);
    const letter = kind === 'loading' ? 'Z' : 'R';
    const make = (index: number, p: RecordPoint | null): WindowBracket => {
      const rf = p?.routerPlannedFrom ?? null;
      const rt = p?.routerPlannedTo ?? null;
      const plan = rf !== null || rt !== null;
      const city = p?.city?.trim() || null;
      const country = p?.countryCode?.trim() || null;
      return {
        kind,
        index,
        label: `${letter}${index}`,
        from: plan ? (rf ?? clientFrom) : clientFrom,
        to: plan ? (rt ?? clientTo) : clientTo,
        source: plan ? 'tmsPlan' : 'client',
        place: city && country ? `${city} (${country})` : (city ?? country),
      };
    };
    if (ofKind.length === 0) out.push(make(1, null));
    else ofKind.forEach((p, i) => out.push(make(i + 1, p)));
  }
  return out;
}
